import { useMemo, useState } from "react";
import { Save, UserRoundCog, UsersRound } from "lucide-react";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import type { AccountRecord, StoreRecord } from "./organization.types";
import { useOrganization, useOrganizationActions } from "./useOrganization";

export function MemberCoachAssignmentPanel({
  storeId = null,
}: {
  storeId?: string | null;
}) {
  const organization = useOrganization();
  const [requestedMemberId, setRequestedMemberId] = useState("");
  const data = organization.data;
  const members = useMemo(
    () =>
      (data?.accounts ?? [])
        .filter(
          (item) =>
            item.role === "member" &&
            item.status === "active" &&
            (!storeId || item.storeId === storeId),
        )
        .sort((left, right) => left.name.localeCompare(right.name, "zh-CN")),
    [data?.accounts, storeId],
  );
  if (!data) return null;
  if (!members.length)
    return <EmptyState title="暂无可分配会员" description="创建并启用会员后可配置多名授课教练。" />;

  const memberId = members.some((item) => item.id === requestedMemberId)
    ? requestedMemberId
    : members[0].id;
  const member = members.find((item) => item.id === memberId)!;
  const delegatedCoachIds = new Set(
    data.assignments
      .filter(
        (assignment) =>
          assignment.memberId === member.id && assignment.status === "active",
      )
      .map((assignment) => assignment.employeeId),
  );
  const eligibleCoaches = data.accounts
    .filter(
      (item) =>
        (item.role === "employee" || item.role === "manager") &&
        item.status === "active" &&
        (item.storeId === member.storeId || delegatedCoachIds.has(item.id)),
    )
    .sort((left, right) => left.name.localeCompare(right.name, "zh-CN"));

  return (
    <Card className="management-card member-coach-assignment">
      <div className="card-heading">
        <UserRoundCog />
        <div>
          <span>主教练＋协作教练</span>
          <h2>会员授课团队</h2>
        </div>
      </div>
      <p className="muted-copy">
        主教练继续负责会员归属、续费预警和课程包；协作教练可使用同一课程包完成排课、签到、扣课、反馈与成长影像，流水记录实际授课教练。
      </p>
      <div className="v4-form">
        <label className="v4-field">
          <span>会员</span>
          <select value={memberId} onChange={(event) => setRequestedMemberId(event.target.value)}>
            {members.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {item.phone} · {storeName(data.stores, item.storeId)}
              </option>
            ))}
          </select>
        </label>
        <CoachConfiguration
          key={member.id}
          member={member}
          coaches={eligibleCoaches}
          stores={data.stores}
        />
      </div>
    </Card>
  );
}

function CoachConfiguration({
  member,
  coaches,
  stores,
}: {
  member: AccountRecord;
  coaches: AccountRecord[];
  stores: StoreRecord[];
}) {
  const actions = useOrganizationActions();
  const initialPrimary = member.coachId ?? "";
  const [primaryCoachId, setPrimaryCoachId] = useState(initialPrimary);
  const [serviceCoachIds, setServiceCoachIds] = useState<string[]>(
    Array.from(
      new Set([
        ...(member.serviceCoachIds ?? []),
        ...(initialPrimary ? [initialPrimary] : []),
      ]),
    ),
  );
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const selectPrimary = (coachId: string) => {
    setPrimaryCoachId(coachId);
    setServiceCoachIds((current) => Array.from(new Set([...current, coachId])));
  };
  const toggleCoach = (coachId: string, checked: boolean) => {
    if (coachId === primaryCoachId && !checked) return;
    setServiceCoachIds((current) =>
      checked
        ? Array.from(new Set([...current, coachId]))
        : current.filter((id) => id !== coachId),
    );
  };
  const save = async () => {
    if (!primaryCoachId || !serviceCoachIds.length) return;
    setNotice("");
    setError("");
    try {
      await actions.setMemberCoaches({
        memberId: member.id,
        primaryCoachId,
        serviceCoachIds,
      });
      setNotice("授课教练已保存；所有已授权教练均可为该会员排课、扣课和提交反馈。");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "授课教练保存失败");
    }
  };
  return (
    <>
      {(notice || error) && (
        <div className={error ? "inline-notice inline-notice--error" : "inline-notice"}>
          {error || notice}
        </div>
      )}
      <label className="v4-field">
        <span>主教练（客户归属与预警负责人）</span>
        <select value={primaryCoachId} onChange={(event) => selectPrimary(event.target.value)}>
          <option value="">请选择</option>
          {coaches.map((coach) => (
            <option key={coach.id} value={coach.id}>
              {coach.name} · {storeName(stores, coach.storeId)}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="member-coach-picker">
        <legend><UsersRound size={15} /> 授课教练（含主教练，最多5名）</legend>
        <div>
          {coaches.map((coach) => {
            const checked = serviceCoachIds.includes(coach.id);
            return (
              <label key={coach.id}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={coach.id === primaryCoachId || (!checked && serviceCoachIds.length >= 5)}
                  onChange={(event) => toggleCoach(coach.id, event.target.checked)}
                />
                <span>{coach.name}<small>{coach.id === primaryCoachId ? "主教练" : storeName(stores, coach.storeId)}</small></span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <Button
        type="button"
        onClick={save}
        disabled={actions.pending || !primaryCoachId || !serviceCoachIds.length}
      >
        <Save size={15} />保存授课团队
      </Button>
    </>
  );
}

function storeName(stores: StoreRecord[], storeId: string | null) {
  return stores.find((store) => store.id === storeId)?.name ?? "总部/跨店";
}
